import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function NotificationOptIn() {
  const [status, setStatus] = useState("checking"); // 'checking' | 'unsupported' | 'off' | 'on' | 'blocked'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported) { setStatus("unsupported"); return; }
      if (Notification.permission === "denied") { setStatus("blocked"); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (cancelled) return;
        setStatus(sub ? "on" : "off");
      } catch {
        if (!cancelled) setStatus("off");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus("blocked"); toast.error("Notifications refusées par le navigateur"); return; }
      const { data } = await api.get("/push/public-key");
      const pub = data?.public_key;
      if (!pub) { toast.error("Le service push n'est pas configuré côté serveur."); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(pub),
      });
      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
          auth:   btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
        },
        user_agent: navigator.userAgent,
      });
      setStatus("on");
      toast.success("Notifications activées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/unsubscribe", { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setStatus("off");
      toast.success("Notifications désactivées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const test = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.info(`Push test envoyé (${data.sent} destinataire${data.sent > 1 ? "s" : ""})`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (status === "unsupported")
    return <div data-testid="notif-unsupported" className="text-xs text-slate-500">Les notifications ne sont pas prises en charge sur ce navigateur.</div>;

  return (
    <div data-testid="notif-optin" className="flex flex-wrap items-center gap-2">
      {status === "on" ? (
        <>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-semibold">
            <Bell className="h-3.5 w-3.5" /> Activées sur cet appareil
          </span>
          <Button size="sm" variant="outline" onClick={test} data-testid="notif-test">Tester</Button>
          <Button size="sm" variant="outline" onClick={disable} disabled={busy} data-testid="notif-disable">
            <BellOff className="h-3.5 w-3.5 mr-1" />Désactiver
          </Button>
        </>
      ) : status === "blocked" ? (
        <span className="text-xs text-rose-600">Les notifications ont été bloquées par votre navigateur. Réactivez-les dans les paramètres du site.</span>
      ) : (
        <Button size="sm" onClick={enable} disabled={busy || status === "checking"}
          data-testid="notif-enable" className="bg-[#0066FF] hover:bg-[#0052CC]">
          <Bell className="h-3.5 w-3.5 mr-1" />
          {busy ? "Activation…" : "Activer les notifications"}
        </Button>
      )}
    </div>
  );
}
